import type { BuilderContent } from "@builder.io/sdk-react";

import { isBuilderReference } from "@/utils/isBuilderReference";
import { isObject } from "@/utils/isObject";

/**
 * Extended Builder.io content type that includes fields present in API responses
 * but not documented in the official type definitions
 */
type BuilderContentWithMetadata = BuilderContent & {
  previewUrl?: string;
  meta?: BuilderContent["meta"] & {
    lastPreviewUrl?: string;
  };
};

type BuilderContentUrlPathMapping = {
  internalUrlPath: string;
  publicUrlPath: string;
};

export function sanitizeBuilderContent<T extends BuilderContentWithMetadata | null>(
  content: T,
  urlPathMapping?: BuilderContentUrlPathMapping
) {
  if (!content) {
    return content;
  }

  const sanitized = structuredClone(content);

  delete sanitized.previewUrl;

  if (sanitized.meta) {
    delete sanitized.meta.lastPreviewUrl;
  }

  if (sanitized.data && typeof sanitized.data === "object") {
    sanitizeNestedReferences(sanitized.data);
  }

  if (urlPathMapping) {
    if (isObject(sanitized.data) && sanitized.data.url === urlPathMapping.internalUrlPath) {
      sanitized.data.url = urlPathMapping.publicUrlPath;
    }
    normalizeTargetingUrlPaths(sanitized, urlPathMapping);
    normalizeStateLocationUrlPaths(sanitized, urlPathMapping);
  }

  return sanitized;
}

function sanitizeNestedReferences(obj: unknown): void {
  if (!isObject(obj)) return;

  for (const value of Object.values(obj)) {
    if (!isObject(value)) continue;

    if (isBuilderReference(value)) {
      value.value = sanitizeBuilderContent(value.value);
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (isObject(item)) {
          sanitizeNestedReferences(item);
        }
      });
    } else {
      sanitizeNestedReferences(value);
    }
  }
}

export function sanitizeBuilderEntries<T extends BuilderContent>(entries: T[]): T[] {
  return entries.map((entry) => sanitizeBuilderContent(entry));
}

function normalizeTargetingUrlPaths(content: unknown, urlPathMapping: BuilderContentUrlPathMapping): void {
  if (!isObject(content)) return;

  if (Array.isArray(content)) {
    content.forEach((entry) => normalizeTargetingUrlPaths(entry, urlPathMapping));
    return;
  }

  if (Array.isArray(content.query)) {
    content.query.forEach((rule) => normalizeTargetingUrlPathRule(rule, urlPathMapping));
  }

  Object.values(content).forEach((value) => normalizeTargetingUrlPaths(value, urlPathMapping));
}

function normalizeTargetingUrlPathRule(
  rule: unknown,
  { internalUrlPath, publicUrlPath }: BuilderContentUrlPathMapping
): void {
  if (!isObject(rule) || rule.property !== "urlPath") return;

  const publicUrlPathPrefix = publicUrlPath.endsWith(internalUrlPath)
    ? publicUrlPath.slice(0, -internalUrlPath.length)
    : null;

  if (!publicUrlPathPrefix) return;

  if (typeof rule.value === "string") {
    rule.value = normalizeTargetingUrlPath(rule.value, internalUrlPath, publicUrlPathPrefix);
  }

  if (Array.isArray(rule.value)) {
    rule.value = rule.value.map((value) =>
      typeof value === "string" ? normalizeTargetingUrlPath(value, internalUrlPath, publicUrlPathPrefix) : value
    );
  }
}

function normalizeTargetingUrlPath(value: string, internalUrlPath: string, publicUrlPathPrefix: string): string {
  return value && value !== "/" && internalUrlPath.startsWith(value) ? `${publicUrlPathPrefix}${value}` : value;
}

function normalizeStateLocationUrlPaths(content: unknown, urlPathMapping: BuilderContentUrlPathMapping): void {
  if (!isObject(content)) return;

  if (Array.isArray(content)) {
    content.forEach((entry) => normalizeStateLocationUrlPaths(entry, urlPathMapping));
    return;
  }

  if (isObject(content.state)) {
    normalizeStateLocation(content.state, urlPathMapping);
  }

  Object.values(content).forEach((value) => normalizeStateLocationUrlPaths(value, urlPathMapping));
}

function normalizeStateLocation(
  state: Record<string, unknown>,
  { internalUrlPath, publicUrlPath }: BuilderContentUrlPathMapping
) {
  if (state.urlPath === internalUrlPath) {
    state.urlPath = publicUrlPath;
  }

  if (!isObject(state.location) || state.location.pathname !== internalUrlPath) return;

  state.location.pathname = publicUrlPath;
  state.location.path = publicUrlPath.split("/").filter(Boolean);

  if (state.location.urlPath === internalUrlPath) {
    state.location.urlPath = publicUrlPath;
  }
}
